import { useState, useEffect, useRef } from "react";
import api from "@/hooks/api/apiClient";
import config from "../../config";

export type CheckStatus = "idle" | "checking" | "available" | "taken" | "error";

// "school-code-lookup" — for students: checks whether a school code EXISTS (available = found)
type CheckType = "email" | "school-code" | "school-name" | "school-code-lookup";

const ENDPOINTS: Record<CheckType, (value: string) => string> = {
  "email":              (v) => `/auth/check/email?email=${encodeURIComponent(v)}`,
  "school-code":        (v) => `/auth/check/school-code?code=${encodeURIComponent(v)}`,
  "school-name":        (v) => `/auth/check/school-name?name=${encodeURIComponent(v)}`,
  "school-code-lookup": (v) => `/schools/code/${encodeURIComponent(v.toUpperCase())}/education-system`,
};

interface Options {
  type: CheckType;
  minLength?: number;
  debounceMs?: number;
}

export function useAvailabilityCheck({ type, minLength = 6, debounceMs = 2000 }: Options) {
  const [status, setStatus] = useState<CheckStatus>("idle");
  const [data, setData] = useState<unknown>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastChecked = useRef<string>("");

  const check = (value: string) => {
    const trimmed = value.trim();

    if (trimmed.length < minLength) {
      setStatus("idle");
      setData(null);
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    if (trimmed === lastChecked.current && status !== "idle") return;

    setStatus("checking");
    setData(null);
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      if (config.appMode === "prototype") {
        setStatus("available");
        return;
      }
      try {
        const res = await api.get(ENDPOINTS[type](trimmed));
        lastChecked.current = trimmed;

        if (type === "school-code-lookup") {
          // 200 = school found → treat as "available" (valid code)
          setData(res.data?.data ?? null);
          setStatus("available");
        } else {
          const available: boolean = res.data?.data?.available;
          setStatus(available ? "available" : "taken");
        }
      } catch (err: unknown) {
        lastChecked.current = trimmed;
        if (
          type === "school-code-lookup" &&
          (err as { response?: { status?: number } })?.response?.status === 404
        ) {
          setStatus("taken"); // code doesn't exist
        } else {
          setStatus("error");
        }
      }
    }, debounceMs);
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const reset = () => { setStatus("idle"); setData(null); lastChecked.current = ""; };

  return { status, check, reset, data };
}
